export const arPageData = {
  stats: [
    { value: "40%", label: "DSO Reduction" },
    { value: "95%", label: "Invoice Recovery" },
    { value: "90%", label: "Less Manual AR Work" },
    { value: "3×", label: "Faster Reconciliation" },
  ],
  statsAccent: "from-cyan-600 to-blue-700",

  challengeTitle: "Late Payments Are Quietly Draining Your Business",
  challengeSubtitle:
    "B2B companies lose an average of 14% of revenue to AR inefficiencies. Here's what's costing you every day.",
  challenges: [
    {
      icon: "🕐",
      title: "Hours Lost on Manual Reconciliation",
      description:
        "Finance teams spend 3–4 hours daily manually matching payments to invoices — time that should go to strategic work.",
    },
    {
      icon: "📉",
      title: "High Days Sales Outstanding (DSO)",
      description:
        "Without automated reminders, customers pay late. Every extra day in DSO ties up working capital you need to grow.",
    },
    {
      icon: "❌",
      title: "Invoice Disputes and Errors",
      description:
        "Manual invoice generation produces errors that cause disputes, payment delays, and damaged customer relationships.",
    },
    {
      icon: "🔍",
      title: "Zero Visibility into Cash Flow",
      description:
        "Without real-time AR dashboards, finance leaders fly blind — unable to forecast cash flow or prioritize collections.",
    },
    {
      icon: "📋",
      title: "Inconsistent Follow-Up",
      description:
        "Overdue accounts don't get chased consistently. Staff forget, prioritise incorrectly, and revenue slips through the cracks.",
    },
    {
      icon: "🔗",
      title: "Disconnected Systems",
      description:
        "AR data locked in spreadsheets, email inboxes, and siloed accounting tools makes it impossible to act quickly.",
    },
  ],
  theme: {
    challengeBg: "bg-slate-900",
    challengeTitle: "text-white",
    challengeSubtitle: "text-slate-400",
    challengeCard: "bg-slate-800/60",
    challengeBorder: "border-slate-700",
    badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-100",
    solutionIconBg: "bg-cyan-50",
    solutionIconColor: "text-cyan-600",
    solutionAccent: "from-cyan-600 to-blue-600",
  },

  solutionTitle: "Automate Your Entire AR Cycle — Invoice to Cash",
  solutionSubtitle:
    "EkkBaz AI handles every step of accounts receivable, giving you predictable cash flow with zero manual effort.",
  solutions: [
    {
      icon: "🤖",
      title: "AI-Powered Invoice Generation",
      description:
        "Generate accurate, branded invoices automatically from order data — eliminating manual entry and invoice errors entirely.",
    },
    {
      icon: "⏰",
      title: "Intelligent Payment Reminders",
      description:
        "AI sends reminders at optimal intervals based on each customer's payment behaviour, maximising on-time payment rates.",
    },
    {
      icon: "⚡",
      title: "Instant Payment Matching",
      description:
        "AI matches incoming payments to open invoices in milliseconds — even with partial payments or mismatched references.",
    },
    {
      icon: "📊",
      title: "Real-Time AR Dashboard",
      description:
        "See every open invoice, overdue account, and cash flow forecast in one live dashboard — no spreadsheets required.",
    },
    {
      icon: "🚨",
      title: "Automated Collections Escalation",
      description:
        "Overdue accounts are automatically escalated through collections workflows, recovering 95% of outstanding invoices.",
    },
    {
      icon: "🔌",
      title: "Seamless Accounting Integration",
      description:
        "Connects to QuickBooks, Xero, SAP, and all major accounting platforms — keeping books accurate and real-time.",
    },
    {
      icon: "🔮",
      title: "Predictive Payment Intelligence",
      description:
        "AI predicts which customers are likely to pay late, enabling proactive intervention before accounts become overdue.",
    },
    {
      icon: "📈",
      title: "Cash Flow Forecasting",
      description:
        "Accurate 30/60/90-day cash flow projections powered by real-time AR data and AI payment behaviour analysis.",
    },
    {
      icon: "🛡️",
      title: "Dispute Resolution Tools",
      description:
        "Built-in dispute management workflows resolve invoice disagreements quickly, protecting customer relationships.",
    },
  ],

  howItWorksTitle: "From Setup to Automated AR in 48 Hours",
  howItWorksSteps: [
    {
      icon: "🔌",
      title: "Connect Your Systems",
      description: "Integrate with your accounting software, ERP, and order management in minutes with pre-built connectors.",
    },
    {
      icon: "⚙️",
      title: "Configure Automation Rules",
      description: "Set your invoice templates, reminder schedules, and escalation workflows — customised to your business.",
    },
    {
      icon: "🚀",
      title: "AI Takes Over",
      description: "EkkBaz AI automatically generates invoices, sends reminders, matches payments, and updates your books.",
    },
    {
      icon: "📈",
      title: "Watch DSO Drop",
      description: "Monitor your AR performance in real time. Most customers see a 40% DSO reduction within 90 days.",
    },
  ],

  outcomeTitle: "The Numbers That Matter",
  outcomeSubtitle:
    "Real results from businesses using EkkBaz AI AR Automation — delivered in the first quarter.",
  outcomes: [
    {
      value: "40%",
      metric: "DSO Reduction",
      description:
        "Average reduction in Days Sales Outstanding — freeing up working capital that was previously trapped in slow payments.",
    },
    {
      value: "95%",
      metric: "Invoice Recovery Rate",
      description:
        "Of all overdue invoices are recovered automatically through AI-driven collections — without human intervention.",
    },
    {
      value: "90%",
      metric: "Less Manual AR Work",
      description:
        "Finance teams reclaim 90% of time previously spent on reconciliation, chasing payments, and manual data entry.",
    },
  ],
  outcomeImage: "/assets/images/products/ar-auto2.png",

  ctaTitle: "Stop Losing Revenue to Slow Payments",
  ctaSubtitle:
    "Book a free consultation and see how EkkBaz AI can reduce your DSO by 40% and eliminate manual AR work — starting this quarter.",
  ctaLabel: "Book a Free Consultation",
  ctaLink: "https://outlook.office.com/book/EkkBazInvestment@EkkBaz.com/?ismsaljsauthenabled",
};

export const arData = [
  {
    id: 1,
    extraLabel: "The Challenge",
    userType: "Late Payments and Cash Flow Gaps Are Killing Your Business",
    description:
      "B2B businesses lose an average of 14% of revenue to late payments, disputed invoices, and manual AR processes. Your team spends hours daily reconciling payments, chasing overdue accounts, and generating invoices — time that should go to growing your business.",
    banner: "/assets/images/products/ar-auto1.png",
  },
  {
    id: 2,
    extraLabel: "Our Solution",
    userType: "Automate Your Entire AR Cycle from Invoice to Cash",
    description:
      "EkkBaz AI's AR Automation platform handles everything from invoice generation and delivery to payment tracking, automated reminders, and reconciliation. AI matches payments to invoices instantly, flags discrepancies, and updates your books in real time — reducing DSO by up to 40% and eliminating 90% of manual AR work.",
    banner: "/assets/images/products/ar-auto2.png",
  },
  {
    id: 3,
    extraLabel: "The Outcome",
    userType: "Predictable Cash Flow, Zero Manual Reconciliation",
    description:
      "With EkkBaz AI AR Automation, businesses reduce Days Sales Outstanding (DSO) by 40%, recover 95% of overdue invoices automatically, and gain AI-powered payment insights that predict customer payment behavior — giving finance teams full control of cash flow with zero manual effort.",
    banner: "/assets/images/products/ar-auto3.png",
  },
];

export const arFAQs = [
  {
    question: "How does EkkBaz AI AR Automation reduce Days Sales Outstanding (DSO)?",
    answer:
      "EkkBaz AI sends automated payment reminders at optimal intervals based on each customer's payment behavior, matches incoming payments to invoices instantly, and escalates overdue accounts automatically — reducing average DSO by up to 40%.",
  },
  {
    question: "Does EkkBaz AI AR Automation integrate with accounting software?",
    answer:
      "Yes. EkkBaz AI integrates with major accounting platforms including QuickBooks, Xero, SAP, and others, ensuring your AR data is always synchronized and your books are always current.",
  },
  {
    question: "How does AI improve payment reconciliation?",
    answer:
      "EkkBaz AI uses machine learning to automatically match incoming payments to open invoices — even when payment amounts, references, or timing don't match exactly — reducing reconciliation time from hours to minutes.",
  },
];
